import type { AttrEntry, BasicAttr, AdvancedAttr, ElementType, ElementAttr, ResonanceSkill, SpiritSkill, StoneColor } from '../types';

/* ===== 基础属性 ===== */
export const BASIC_ATTRS: Record<BasicAttr, { label: string; baseValue: number }> = {
    attack: { label: '攻击力', baseValue: 10 },
    hp: { label: '气血', baseValue: 50 },
    defense: { label: '防御', baseValue: 8 },
    constitution: { label: '体质', baseValue: 5 },
};

/* ===== 进阶属性 ===== */
export const ADVANCED_ATTRS: Record<AdvancedAttr, { label: string; baseValue: number }> = {
    yuanyu: { label: '元御', baseValue: 1 },
    crit_rate: { label: '会心', baseValue: 1 },
    crit_dmg: { label: '会心效果', baseValue: 1 },
    specialty_rate: { label: '专精', baseValue: 1 },
    breath_rate: { label: '调息', baseValue: 1 },
    hit: { label: '命中', baseValue: 1 },
};

/* ===== 元素属性 ===== */
export const ELEMENT_LABELS: Record<ElementType, string> = {
    fire: '天火',
    ice: '玄冰',
    wood: '苍木',
    thunder: '神雷',
};

export const ELEMENT_ATTR_LABELS: Record<ElementAttr, string> = {
    enhance: '增强',
    resist: '抵抗',
    bonus: '增伤',
};

/* ===== 灵蕴共鸣技能 ===== */
export const RESONANCE_LABELS: Record<ResonanceSkill, string> = {
    fire_meteor: '天火陨星',
    fire_ring: '赤焰天环',
    fire_wildfire: '烈火燎原',
    fire_burn: '烈焰焚身',
    fire_erupt: '神火迸发',
    ice_frost: '凛霜寒涌',
    ice_spike: '寒晶刺',
    ice_wave: '寒潮冰涌',
    ice_rain: '霜刺寒雨',
    ice_break: '霜寒破裂',
    wood_rot: '腐木瘴风',
    wood_puppet: '苍林浮生',
    wood_dice: '神木骰',
    wood_spirit: '木引青灵',
    wood_crack: '裂地崩',
    thunder_halberd: '惊雷戟',
    thunder_shock: '雷霆震击',
    thunder_pearl: '五雷珠',
    thunder_bless: '天雷护佑',
    thunder_sky: '九霄雷动',
};

/* ===== 灵蕴技（匠心石技能） ===== */
export const SPIRIT_SKILL_LABELS: Record<SpiritSkill, string> = {
    fire_skill: '灼灼天炎',
    ice_skill: '凝冰霜华',
    wood_skill: '青芜浮生',
    thunder_skill: '雷佑灵光',
};

export const SPIRIT_SKILL_DESCS: Record<SpiritSkill, string> = {
    fire_skill: '召唤一枚陨星，短暂延迟后在目标位置造成天火伤害',
    ice_skill: '引导霜冻射线，对前方区域所有敌人持续造成固定玄冰伤害',
    wood_skill: '召唤巨型苍木树人，立刻冲击波造成伤害+治疗；随后持续攻击敌人+治疗队友',
    thunder_skill: '引动雷电震击周围所有敌人和队友，造成神雷伤害+恢复，持续期间定时触发连锁闪电，自身获得护盾',
};

/* ===== 颜色对应的属性数量 ===== */
export const COLOR_ATTR_COUNT: Record<StoneColor, number> = {
    white: 1,
    green: 2,
    blue: 3,
    purple: 4,
};

/* ===== 颜色标签 ===== */
export const COLOR_LABELS: Record<StoneColor, string> = {
    white: '白色',
    green: '绿色',
    blue: '蓝色',
    purple: '紫色',
};

/* ===== 颜色对应的CSS颜色 ===== */
export const COLOR_CSS: Record<StoneColor, string> = {
    white: '#e8e8e8',
    green: '#4ade80',
    blue: '#60a5fa',
    purple: '#c084fc',
};

/* ===== 元素对应的CSS颜色 ===== */
export const ELEMENT_CSS: Record<ElementType, string> = {
    fire: '#ef4444',
    ice: '#38bdf8',
    wood: '#22c55e',
    thunder: '#a855f7',
};

/* ===== 生成随机属性 ===== */
let attrIdCounter = 0;

function nextAttrId(): string {
    return `attr_${++attrIdCounter}`;
}

/**
 * 白色基础属性池：会心(会)、专精(专)、调息(调)、元御(御)
 * 白色石头只从这4种属性中随机取1条，所有属性值统一为+1
 */
const WHITE_ATTR_POOL: AdvancedAttr[] = ['crit_rate', 'specialty_rate', 'breath_rate', 'yuanyu'];

/** 为机巧石生成随机属性列表（按用户指定规则） */
export function generateRandomAttrs(color: StoneColor, element: ElementType, excludeIds: string[] = []): AttrEntry[] {
    const count = COLOR_ATTR_COUNT[color];
    const elementAttrs: ElementAttr[] = ['enhance', 'resist'];
    // 按石头属性前缀筛选对应的灵蕴共鸣技能
    const resonances = (Object.keys(RESONANCE_LABELS) as ResonanceSkill[]).filter(r => {
        return r.startsWith('fire') && element === 'fire'
            || r.startsWith('ice') && element === 'ice'
            || r.startsWith('wood') && element === 'wood'
            || r.startsWith('thunder') && element === 'thunder';
    });

    const attrs: AttrEntry[] = [];
    const usedKeys = new Set<string>();

    // 辅助：从白色属性池随机取一条（不重复），所有属性值统一为+1
    function pickWhiteAttr(): AttrEntry {
        const available = WHITE_ATTR_POOL.filter(k => !usedKeys.has(k));
        const key = available[Math.floor(Math.random() * available.length)] || WHITE_ATTR_POOL[0];
        usedKeys.add(key);
        return {
            id: nextAttrId(),
            label: ADVANCED_ATTRS[key].label,
            category: 'advanced',
            value: 1,
        };
    }

    // 辅助：生成随机特效词条（灵蕴共鸣）
    function pickResonance(): AttrEntry {
        const resonance = resonances[Math.floor(Math.random() * resonances.length)];
        const elemType = resonance.startsWith('fire') ? 'fire'
            : resonance.startsWith('ice') ? 'ice'
                : resonance.startsWith('wood') ? 'wood'
                    : 'thunder';
        return {
            id: nextAttrId(),
            label: RESONANCE_LABELS[resonance],
            category: 'resonance',
            value: 1,
            element: elemType,
            resonance,
        };
    }

    // 辅助：生成元素增强或抗性，使用石头的前缀元素
    function pickElementAttr(): AttrEntry {
        const eAttr = elementAttrs[Math.floor(Math.random() * elementAttrs.length)];
        return {
            id: nextAttrId(),
            label: `${ELEMENT_LABELS[element]}${ELEMENT_ATTR_LABELS[eAttr]}`,
            category: 'element',
            value: 1,
            element: element,
            elementAttr: eAttr,
        };
    }

    // 根据颜色生成属性列表（值统一为+1）
    // 白色: 1条来自 {会心, 专精, 调息, 元御}
    // 绿色: 1条白色 + 1条随机特效词条（灵蕴共鸣）
    // 蓝色: 1条白色 + 1条随机特效词条（灵蕴共鸣）+ 1条元素增强/抗性
    // 紫色: 1条白色 + 1条随机特效词条（灵蕴共鸣）+ 1条元素增强/抗性 + 1条随机特效词条（灵蕴共鸣）
    switch (color) {
        case 'white':
            attrs.push(pickWhiteAttr());
            break;
        case 'green':
            attrs.push(pickWhiteAttr());
            attrs.push(pickResonance());
            break;
        case 'blue':
            attrs.push(pickWhiteAttr());
            attrs.push(pickResonance());
            attrs.push(pickElementAttr());
            break;
        case 'purple':
            attrs.push(pickWhiteAttr());
            attrs.push(pickResonance());
            attrs.push(pickElementAttr());
            attrs.push(pickResonance());
            break;
    }

    return attrs;
}

/** 为匠心石生成固定灵蕴技能 */
export function generateCraftSkill(element: ElementType): AttrEntry {
    const skillMap: Record<ElementType, SpiritSkill> = {
        fire: 'fire_skill',
        ice: 'ice_skill',
        wood: 'wood_skill',
        thunder: 'thunder_skill',
    };
    const skill = skillMap[element];
    return {
        id: nextAttrId(),
        label: SPIRIT_SKILL_LABELS[skill],
        category: 'resonance',
        value: 1,
        element,
        spiritSkill: skill,
    };
}